import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight, Copy, Pencil, Plus, Trash2, X } from "lucide-react";
import { InlineLoadError } from "@/components/feedback/InlineLoadError";
import { QuotationCopyOverwriteModal } from "@/components/quotation/QuotationCopyOverwriteModal";
import { QuickDateChooser } from "@/components/quotation/QuickDateChooser";
import {
  SpecialQuotationDrawer,
  type SpecialDrawerState,
} from "@/components/quotation/SpecialQuotationDrawer";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import {
  useCopySpecialInto,
  useDeleteSpecialQuotation,
  useFindQuotationByDate,
  useSpecialQuotations,
} from "@/features/quotation/hooks";
import type { DailyQuotation } from "@/features/quotation/types";
import {
  displayToIsoDate,
  formatQuotationHistoryDate,
  isoToDisplayDate,
} from "@/features/quotation/dateInput";
import {
  getCuritibaDate,
  isCurrentQuotationOperation,
} from "@/features/quotation/publication";
import { formatDateBR } from "@/lib/timeAgo";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { targetBadge } from "./specialQuotationTargets";
import "./quotation-management.css";

export function QuotationSpecialsDialog({
  quotation,
  canDelete,
  onClose,
}: {
  quotation: DailyQuotation | null;
  canDelete: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const parentId = quotation?.id ?? "";
  const specialsQuery = useSpecialQuotations(parentId, Boolean(quotation));
  const findQuotationMutation = useFindQuotationByDate();
  const deleteMutation = useDeleteSpecialQuotation(parentId);
  const copyMutation = useCopySpecialInto(parentId);
  const [drawer, setDrawer] = useState<SpecialDrawerState | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [copying, setCopying] = useState<DailyQuotation | null>(null);
  const [copyClosing, setCopyClosing] = useState(false);
  const [copyDateText, setCopyDateText] = useState("");
  const [copyTarget, setCopyTarget] = useState<DailyQuotation | null>(null);
  const [childReady, setChildReady] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const returnFocusLabel = useRef<string | null>(null);
  const operation = useRef(0);
  const busy = useRef(false);
  const activeParentId = useRef(parentId);
  const activeCopying = useRef(copying);
  const activeCopyTarget = useRef(copyTarget);
  activeParentId.current = parentId;
  activeCopying.current = copying;
  activeCopyTarget.current = copyTarget;
  const [error, setError] = useState<string | null>(null);
  const mutationPending =
    findQuotationMutation.isPending ||
    deleteMutation.isPending ||
    copyMutation.isPending;

  const close = () => {
    if (busy.current || mutationPending) return;
    operation.current += 1;
    onClose();
  };
  useDialogFocus(dialogRef, close, {
    enabled: Boolean(quotation) && !drawer && !copyTarget,
  });

  useEffect(() => {
    if (drawer || copyTarget) setChildReady(true);
  }, [drawer, copyTarget]);

  function closeCopy() {
    operation.current += 1;
    setChildReady(false);
    setCopyClosing(true);
  }

  useEffect(() => {
    if (!copyClosing) return;
    const timeout = window.setTimeout(() => {
      setCopying(null);
      setCopyDateText("");
      setCopyTarget(null);
      setError(null);
      setCopyClosing(false);
    }, 160);
    return () => window.clearTimeout(timeout);
  }, [copyClosing]);

  useEffect(() => {
    if (drawer || copyTarget || !returnFocusLabel.current) return;
    const label = returnFocusLabel.current;
    const timeout = window.setTimeout(() => {
      Array.from(document.querySelectorAll<HTMLButtonElement>("button"))
        .find((button) => button.getAttribute("aria-label") === label)
        ?.focus();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [copyTarget, drawer]);

  async function chooseCopyTarget() {
    const date = displayToIsoDate(copyDateText);
    if (!date || busy.current || mutationPending) return;
    busy.current = true;
    setError(null);
    const generation = ++operation.current;
    const originalParentId = parentId;
    const originalSpecialId = copying?.id ?? "";
    try {
      const target = await findQuotationMutation.mutateAsync({
        date,
        excludeId: parentId,
      });
      if (
        !isCurrentQuotationOperation({
          quotationId: originalParentId,
          activeQuotationId: activeParentId.current,
          generation,
          activeGeneration: operation.current,
        }) ||
        activeCopying.current?.id !== originalSpecialId
      )
        return;
      if (!target || target.is_special) {
        setError("Não existe cotação para a data selecionada.");
        return;
      }
      setChildReady(false);
      setCopyTarget(target);
    } catch (cause) {
      if (
        !isCurrentQuotationOperation({
          quotationId: originalParentId,
          activeQuotationId: activeParentId.current,
          generation,
          activeGeneration: operation.current,
        }) ||
        activeCopying.current?.id !== originalSpecialId
      )
        return;
      setError(
        getUserFacingErrorMessage(
          cause,
          "Não foi possível verificar a data selecionada.",
        ),
      );
    } finally {
      busy.current = false;
    }
  }

  async function copySpecial() {
    if (!copying || !copyTarget || busy.current || mutationPending) return;
    busy.current = true;
    setError(null);
    const generation = ++operation.current;
    const originalParentId = parentId;
    const originalSpecialId = copying.id;
    const originalTargetId = copyTarget.id;
    try {
      await copyMutation.mutateAsync({
        specialId: copying.id,
        targetId: copyTarget.id,
      });
      if (
        !isCurrentQuotationOperation({
          quotationId: originalParentId,
          activeQuotationId: activeParentId.current,
          generation,
          activeGeneration: operation.current,
        }) ||
        activeCopying.current?.id !== originalSpecialId ||
        activeCopyTarget.current?.id !== originalTargetId
      )
        return;
      closeCopy();
    } catch (cause) {
      if (
        !isCurrentQuotationOperation({
          quotationId: originalParentId,
          activeQuotationId: activeParentId.current,
          generation,
          activeGeneration: operation.current,
        })
      )
        return;
      setError(
        getUserFacingErrorMessage(
          cause,
          "Não foi possível copiar a cotação especial.",
        ),
      );
    } finally {
      busy.current = false;
    }
  }

  return (
    <>
      {quotation && !drawer && !copyTarget && typeof document !== "undefined"
        ? createPortal(
            <div
              className="lnk-sheet-scrim lnk-management-modal-scrim is-open quotation-management-scrim"
              onClick={close}
            >
              <section
                ref={dialogRef}
                className="lnk-management-modal lnk-specials-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Cotações especiais"
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
              >
                <header>
                  <div>
                    <span className="lnk-kicker">COTAÇÕES ESPECIAIS</span>
                    <h2>{formatQuotationHistoryDate(quotation.date)}</h2>
                  </div>
                  <button
                    type="button"
                    className="lnk-modal-close"
                    aria-label="Fechar especiais"
                    onClick={close}
                    disabled={mutationPending}
                  >
                    <X size={18} />
                  </button>
                </header>
                <div
                  className={`lnk-management-list ${!specialsQuery.data?.length && !specialsQuery.isLoading && !specialsQuery.isError ? "is-empty" : ""}`}
                >
                  {specialsQuery.isLoading ? (
                    <p>Carregando especiais...</p>
                  ) : null}
                  {specialsQuery.isError ? (
                    <InlineLoadError
                      title="Cotações especiais indisponíveis"
                      message={getUserFacingErrorMessage(
                        specialsQuery.error,
                        "Não foi possível carregar as cotações especiais.",
                      )}
                      onRetry={() => void specialsQuery.refetch()}
                      isRetrying={specialsQuery.isFetching}
                    />
                  ) : null}
                  {(specialsQuery.data ?? []).map((special) => {
                    const names =
                      special.targets
                        ?.map((target) => target.name)
                        .filter(Boolean)
                        .join(", ") || "Alvos selecionados";
                    const badges = Array.from(
                      new Set((special.targets ?? []).map(targetBadge)),
                    );
                    return (
                      <article
                        key={special.id}
                        onClick={(event) => {
                          if (
                            (event.target as Element).closest(
                              "button, input, .quotation-special-confirm, .quotation-special-copy-date",
                            )
                          )
                            return;
                          if (!busy.current && !mutationPending)
                            navigate({
                              to: `/cotacoes/${special.id}` as string,
                            });
                        }}
                      >
                        <span>
                          <button
                            type="button"
                            className="quotation-special-open"
                            aria-label={`Abrir itens de ${names}`}
                            disabled={mutationPending}
                            onClick={() => {
                              if (busy.current || mutationPending) return;
                              navigate({
                                to: `/cotacoes/${special.id}` as string,
                              });
                            }}
                          >
                            <strong>{names}</strong>
                            <ChevronRight size={16} aria-hidden="true" />
                          </button>
                          <small>{badges.join(" · ")}</small>
                        </span>
                        <div>
                          <button
                            type="button"
                            className="edit-action"
                            aria-label={`Editar ${names}`}
                            disabled={mutationPending}
                            onClick={() => {
                              if (busy.current || mutationPending || !quotation)
                                return;
                              returnFocusLabel.current = `Editar ${names}`;
                              setChildReady(false);
                              setDrawer({
                                mode: "edit",
                                parent: quotation,
                                special,
                              });
                            }}
                          >
                            <Pencil size={15} /> Editar
                          </button>
                          <button
                            type="button"
                            className="copy-action"
                            aria-label={`Copiar ${names}`}
                            disabled={mutationPending}
                            onClick={() => {
                              if (busy.current || mutationPending) return;
                              returnFocusLabel.current = `Copiar ${names}`;
                              operation.current += 1;
                              setCopyClosing(false);
                              setCopyTarget(null);
                              setChildReady(false);
                              setCopying(special);
                              setCopyDateText("");
                              setError(null);
                            }}
                          >
                            <Copy size={15} /> Copiar
                          </button>
                          {canDelete ? (
                            <button
                              type="button"
                              className="delete-action"
                              aria-label={`Excluir ${names}`}
                              disabled={mutationPending}
                              onClick={() => {
                                if (!busy.current && !mutationPending)
                                  setConfirmDeleteId(special.id);
                              }}
                            >
                              <Trash2 size={15} /> Excluir
                            </button>
                          ) : null}
                        </div>
                        {confirmDeleteId === special.id ? (
                          <div
                            className="quotation-special-confirm"
                            role="alertdialog"
                            aria-label="Confirmar exclusão"
                          >
                            <span>Excluir esta cotação especial?</span>
                            <button
                              type="button"
                              disabled={mutationPending}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              disabled={mutationPending}
                              onClick={async () => {
                                if (busy.current || mutationPending) return;
                                busy.current = true;
                                setError(null);
                                const generation = ++operation.current;
                                const originalParentId = parentId;
                                try {
                                  await deleteMutation.mutateAsync(special.id);
                                  if (
                                    !isCurrentQuotationOperation({
                                      quotationId: originalParentId,
                                      activeQuotationId: activeParentId.current,
                                      generation,
                                      activeGeneration: operation.current,
                                    })
                                  )
                                    return;
                                  setConfirmDeleteId(null);
                                } catch (cause) {
                                  if (
                                    !isCurrentQuotationOperation({
                                      quotationId: originalParentId,
                                      activeQuotationId: activeParentId.current,
                                      generation,
                                      activeGeneration: operation.current,
                                    })
                                  )
                                    return;
                                  setError(
                                    getUserFacingErrorMessage(
                                      cause,
                                      "Não foi possível excluir a cotação especial.",
                                    ),
                                  );
                                } finally {
                                  busy.current = false;
                                }
                              }}
                            >
                              Excluir
                            </button>
                          </div>
                        ) : null}
                        {copying?.id === special.id ? (
                          <div
                            className={`quotation-special-copy-date ${copyClosing ? "is-closing" : ""}`}
                          >
                            <QuickDateChooser
                              customDateText={copyDateText}
                              confirmLabel="Continuar"
                              isPending={mutationPending}
                              onCustomDateChange={setCopyDateText}
                              onPickToday={() =>
                                setCopyDateText(
                                  isoToDisplayDate(getCuritibaDate()),
                                )
                              }
                              onPickTomorrow={() =>
                                setCopyDateText(
                                  isoToDisplayDate(
                                    getCuritibaDate(
                                      new Date(
                                        Date.now() + 24 * 60 * 60 * 1000,
                                      ),
                                    ),
                                  ),
                                )
                              }
                              onConfirm={chooseCopyTarget}
                              onCancel={() => {
                                if (!busy.current && !mutationPending)
                                  closeCopy();
                              }}
                              showCancelIcon
                            />
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                  {!specialsQuery.isLoading &&
                  !specialsQuery.isError &&
                  (specialsQuery.data?.length ?? 0) === 0 ? (
                    <p>Nenhuma cotação especial.</p>
                  ) : null}
                </div>
                {error ? (
                  <p className="quotation-dialog-error" role="alert">
                    {error}
                  </p>
                ) : null}
                <footer>
                  <span>{specialsQuery.data?.length ?? 0} especiais</span>
                  <button
                    type="button"
                    className="primary"
                    disabled={mutationPending}
                    onClick={() => {
                      if (busy.current || mutationPending || !quotation) return;
                      returnFocusLabel.current = "Nova especial";
                      setChildReady(false);
                      setDrawer({ mode: "create", parent: quotation });
                    }}
                  >
                    <Plus size={16} /> Nova especial
                  </button>
                </footer>
              </section>
            </div>,
            document.body,
          )
        : null}
      <SpecialQuotationDrawer
        state={childReady ? drawer : null}
        onClose={() => {
          setChildReady(false);
          setDrawer(null);
        }}
      />
      <QuotationCopyOverwriteModal
        target={copying && childReady ? copyTarget : null}
        copyError={error}
        isPending={copyMutation.isPending}
        title="Copiar cotação especial"
        description={
          copyTarget
            ? `A cotação especial será copiada para ${formatDateBR(copyTarget.date)}.`
            : undefined
        }
        confirmLabel="Copiar especial"
        onCancel={() => {
          if (busy.current || mutationPending) return;
          operation.current += 1;
          setChildReady(false);
          setCopyTarget(null);
        }}
        onConfirm={copySpecial}
      />
    </>
  );
}
